import { getOptimalTextColor } from './optimalTextColorGenerator';
import { getRawNeutralPalette } from 'mini-theme/lib/utils/getRawNeutralPalette';
import type { AccessibleTextScheme, ColorScheme } from '../schema/ColorScheme';
import { AccessibleTextSchemeBackground } from '../schema/ColorScheme';

/**
 * Function to convert ColorScheme into color and backgroundColor CSS properties with the schemePrimary scheme color.
 */
export function colorSchemeToCss(scheme: ColorScheme): React.CSSProperties {
    return {
        backgroundColor: scheme.schemePrimary,
        color: scheme.accessibleText ? scheme.accessibleText.schemePrimaryBackground : scheme.text,
    };
}

/**
 * Function to convert ColorScheme into border CSS properties with the schemePrimary scheme color.
 */
export function colorSchemeToCssEmpty(scheme: ColorScheme): React.CSSProperties {
    return {
        borderColor: scheme.schemePrimary,
        color: 'transparent',
    };
}

/**
 * Function to convert ColorScheme into color and backgroundColor CSS properties for the calendar surface with the schemeLight scheme color.
 */
export function colorSchemeToDarkCss(scheme: ColorScheme): React.CSSProperties {
    return {
        backgroundColor: scheme.schemeDarkAlt,
        color: scheme.accessibleText ? scheme.accessibleText.schemePrimaryBackground : scheme.text,
    };
}

/**
 * Function to convert ColorScheme into color and backgroundColor CSS properties for the calendar surface with the schemeLight scheme color.
 */
export function colorSchemeToInvertCss(scheme: ColorScheme): React.CSSProperties {
    return {
        backgroundColor: scheme.schemeLighter,
        color: scheme.accessibleText
            ? scheme.accessibleText.schemeLighterBackground
            : scheme.textAlt,
    };
}

/**
 * Function to convert ColorScheme into color and backgroundColor CSS properties for the calendar surface with the schemeLight scheme color.
 */
export function colorSchemeToMediumCss(scheme: ColorScheme): React.CSSProperties {
    return {
        backgroundColor: scheme.schemeLight,
        color: scheme.accessibleText ? scheme.accessibleText.schemePrimaryBackground : scheme.text,
    };
}

/**
 * Function to convert ColorScheme into color and backgroundColor CSS properties for the calendar surface with the schemeLight scheme color.
 */
export function colorSchemeToLightCss(scheme: ColorScheme): React.CSSProperties {
    return {
        backgroundColor: scheme.schemeLighter,
        color: scheme.accessibleText ? scheme.accessibleText.schemePrimaryBackground : scheme.text,
    };
}

export function getAccessibleTextColor(
    scheme: ColorScheme,
    background: AccessibleTextSchemeBackground = AccessibleTextSchemeBackground.msColorWhite
): string {
    switch (background) {
        case AccessibleTextSchemeBackground.schemePrimary:
            return scheme.accessibleText
                ? scheme.accessibleText.schemePrimaryBackground
                : scheme.schemeLighterAlt;
        case AccessibleTextSchemeBackground.schemeLighter:
            return scheme.accessibleText
                ? scheme.accessibleText.schemeLighterBackground
                : scheme.schemePrimary;
        case AccessibleTextSchemeBackground.msColorNeutralLighter:
            return scheme.accessibleText
                ? scheme.accessibleText.msColorNeutralLighterBackground
                : scheme.schemePrimary;
        case AccessibleTextSchemeBackground.msColorWhite:
        default:
            return scheme.accessibleText
                ? scheme.accessibleText.msColorWhiteBackground
                : scheme.schemePrimary;
    }
}

/**
 * Calculates accessible passing text colors for various background colors. Uses the color scheme as a base and adjusts the color as necessary to get
 * minimum contrast for the given backgrounds.
 * @param colorScheme the color scheme
 */
export function calculateAccessibleText(colorScheme: ColorScheme): AccessibleTextScheme {
    const neutralPalette = getRawNeutralPalette();
    return {
        schemePrimaryBackground: getOptimalTextColor(
            colorScheme.schemePrimary,
            colorScheme,
            neutralPalette.textOnAccent
        ),
        schemeLighterBackground: getOptimalTextColor(
            colorScheme.schemeLighter,
            colorScheme,
            neutralPalette.textOnAccent
        ),
        msColorWhiteBackground: getOptimalTextColor(
            neutralPalette.surfacePrimary,
            colorScheme,
            neutralPalette.textOnAccent
        ),
        msColorNeutralLighterBackground: getOptimalTextColor(
            neutralPalette.surfaceTertiary,
            colorScheme,
            neutralPalette.textOnAccent
        ),
    };
}
